import { useEffect, useRef } from 'react';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import {
  findFlowSlotForControlExpandId,
  resolveFlowControlExpandIdFromCueRow,
  scrollFlowControlSegmentIntoView,
} from '@/helpers/flow-control-playing-segment.helper';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useFlowControlMediaExpandStore } from '@/state/stores/use-flow-control-media-expand-store';
import { useFlowControlSimulationCueStore } from '@/state/stores/use-flow-control-simulation-cue-store';
import { useFlowControlPlayingSegmentExpandId } from './use-flow-control-playing-segment-expand-id';

type UseFlowControlAutoFollowPlayingSegmentOptions = {
  enabled: boolean;
  isWideScreen: boolean;
  slots?: FlowSlotDto[];
  onSlotSelect?: (slot: FlowSlotDto, isUserInitiated?: boolean) => void;
};

/** Auto-expand and scroll the list to the segment that is currently playing (no auto-open overlay). */
export function useFlowControlAutoFollowPlayingSegment({
  enabled,
  isWideScreen,
  slots,
  onSlotSelect,
}: UseFlowControlAutoFollowPlayingSegmentOptions) {
  const playingExpandId = useFlowControlPlayingSegmentExpandId();
  const expandId = useFlowControlMediaExpandStore((s) => s.expandId);
  const expandSource = useFlowControlMediaExpandStore((s) => s.expandSource);
  const setExpandId = useFlowControlMediaExpandStore((s) => s.setExpandId);
  const liveRunning = useFlowStore(
    (s) => s.flowPlan?.liveContext?.isRunning ?? s.flow?.liveContext?.isRunning ?? false,
  );
  const simulationRows = useFlowControlSimulationCueStore((s) => s.rows);
  const simulationPointer = useFlowControlSimulationCueStore((s) => s.pointer);
  const lastScrolledExpandIdRef = useRef<string | null>(null);
  const onSlotSelectRef = useRef(onSlotSelect);

  useEffect(() => {
    onSlotSelectRef.current = onSlotSelect;
  }, [onSlotSelect]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const followExpandId =
      playingExpandId ??
      (!liveRunning && expandId == null && expandSource !== 'manual'
        ? resolveFlowControlExpandIdFromCueRow(simulationRows[simulationPointer])
        : null);

    if (!followExpandId) {
      return;
    }
    if (expandSource === 'manual' && expandId != null && expandId !== followExpandId) {
      return;
    }
    if (expandId !== followExpandId || expandSource !== 'playing') {
      setExpandId(followExpandId, 'playing');
    }

    // Narrow: keep the list as the default surface — do not auto-open the perform overlay.
    if (isWideScreen) {
      return;
    }

    const slot = findFlowSlotForControlExpandId(slots, followExpandId);
    if (slot) {
      onSlotSelectRef.current?.(slot, false);
    }
  }, [
    enabled,
    expandId,
    expandSource,
    isWideScreen,
    liveRunning,
    playingExpandId,
    setExpandId,
    simulationPointer,
    simulationRows,
    slots,
  ]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const scrollTarget = playingExpandId ?? expandId;
    if (!scrollTarget || lastScrolledExpandIdRef.current === scrollTarget) {
      return;
    }
    lastScrolledExpandIdRef.current = scrollTarget;
    requestAnimationFrame(() => {
      scrollFlowControlSegmentIntoView(scrollTarget);
    });
  }, [enabled, expandId, playingExpandId]);
}
